import { useEffect, useRef, useState } from 'react';

import useSWR, { ConfigInterface } from 'swr';

const SWR_CONFIG: ConfigInterface = {
  revalidateOnFocus: false, // No recarga al volver a la pestaña
  dedupingInterval: 4000, // Evita llamadas duplicadas en 4s
  revalidateOnReconnect: true, // Reintenta si se pierde conexión
  refreshInterval: 0, // No refresca automáticamente
  shouldRetryOnError: true, // Reintenta llamadas fallidas
  errorRetryCount: 3, // Hasta 3 reintentos por error
  errorRetryInterval: 3000, // Espera 3s entre reintentos
};

type FetchEntityOptions<T> = {
  sideEffects?: (data: T | undefined) => void;
  swrOptions?: Partial<ConfigInterface>;
};

/**
 * Hook genérico para GET/fetch de cualquier entidad
 * - Maneja loading y error
 * - Devuelve mutate para refrescar cache SWR
 * - Efectos colaterales opcionales
 * - Configuración SWR estándar (personalizable via swrOptions)
 */
export const useFetchEntity = <T>(
  key: string | null,
  fetcher: (key: string) => Promise<T | undefined>,
  options?: FetchEntityOptions<T> | ((data: T | undefined) => void),
) => {
  // Soportar firma anterior (sideEffects como tercer parámetro directo)
  const normalizedOptions: FetchEntityOptions<T> = typeof options === 'function'
    ? { sideEffects: options }
    : options ?? {};

  const { sideEffects, swrOptions } = normalizedOptions;

  const { data, error, isValidating, mutate } = useSWR<T | undefined>(
    key,
    fetcher,
    { ...SWR_CONFIG, ...swrOptions },
  );

  // Usar ref para estabilizar sideEffects y evitar re-ejecuciones innecesarias
  const sideEffectsRef = useRef(sideEffects);

  useEffect(() => {
    sideEffectsRef.current = sideEffects;
  }, [sideEffects]);

  useEffect(() => {
    if (!isValidating && sideEffectsRef.current) {
      sideEffectsRef.current(data);
    }
  }, [data, isValidating]);

  return { data, error, isValidating, mutate };
};

/**
 * Hook genérico para POST/CREATE de cualquier entidad
 * - Maneja loading y error
 * - Refresca automáticamente la cache SWR con mutate
 * - Efectos colaterales opcionales (callback)
 */

export const useCreateEntity = <TBody, TResult>(createFn: (body: TBody) => Promise<TResult>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = async (
    body: TBody,
    sideEffect?: (result: TResult, body: TBody) => void | Promise<void>,
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await createFn(body);

      if (sideEffect) await sideEffect(result, body);

      return result;
    } catch (err) {
      setError(err as Error);
      console.error(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { create, isLoading, error };
};
